import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSocket } from 'sonata-platform-components';
import { ButtonUI, Prompt, showNotification, TextArea } from 'sonata-react-components';

import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';
import { getInventoryItems } from '@/services/common.service';
import { AppAndSiteMarketplaceImport } from '../AppAndSiteMarketplaceImport/AppAndSiteMarketplaceImport';
import { applyGenericEntries, classifyGenericEntries } from './helpers/genericEntries.helper';
import { notifyGenericEntriesResult } from './helpers/genericEntriesNotifications.helper';
import { classifySpecificEntries } from './helpers/specificEntries.helper';
import { deleteStrictRows, getInventoryRows, mergeStrictEntries } from './helpers/strictSearches.helper';
import {
  filterRowsBySearch,
  filterSelectionBySearch,
  getSelectedIds,
  getSelectionKey,
  parseBulkEntries
} from './Inventory.helpers';
import { InventoryDescription } from './InventoryDescription/InventoryDescription';
import { InventoryList } from './InventoryList/InventoryList';

import './inventory.scss';

import type { AppSitePublishersConfiguration, AppSiteSearchEntry } from '@/contexts/Targeting/TargetingContext';
import type { AppSiteMarketplaceType, ImportStatus, InventoryOperation } from '../AppAndSiteMarketplace.types';

type State = {
  deleteId?: string;
  deleteType?: string;
  importStatus: ImportStatus;
  /** Entries of the textarea that could not be added because they are not valid. */
  invalidEntries?: string[];
  isLoading: boolean;
  isPromptOpen: boolean;
  operation: InventoryOperation;
  searchText?: string;
  selectedList: string[];
  socketItems?: string[];
  socketResults?: AppSiteSearchEntry[];
  text?: string;
};

type Props = {
  entityId: string;
  id: string;
  sessionId: string;
  type: AppSiteMarketplaceType;
};

const GENERIC_TEXT_MAX_LENGTH = 100000;
const SPECIFIC_TEXT_MAX_LENGTH = 1000;

export function Inventory({ entityId, id, sessionId, type }: Props) {
  const { formatMessage } = useIntl();
  const componentId = `AppAndSiteMarketplaceID_${type}`;

  const { offEvent, onEvent } = useSocket();

  const {
    setAppSiteConfigurationProps,
    targeting: { appSiteConfiguration },
    user
  } = useTargetingContext();

  const [
    {
      deleteId,
      deleteType,
      importStatus,
      invalidEntries,
      isLoading,
      isPromptOpen,
      operation,
      searchText,
      selectedList,
      socketItems,
      socketResults,
      text
    },
    setState
  ] = useState<State>({
    deleteId: undefined,
    deleteType: undefined,
    importStatus: 'idle',
    isLoading: false,
    isPromptOpen: false,
    operation: 'include',
    selectedList: [],
    socketItems: undefined,
    text: undefined
  });

  const config: AppSitePublishersConfiguration = appSiteConfiguration;
  const isInclude = operation === 'include';

  useEffect(() => {
    if (type !== 'specific') {
      return;
    }
    onEvent(componentId, 'inventoryItemsBatchProcess_specific', (data: any) => {
      if (data.status === 'error') {
        showNotification(
          'error',
          formatMessage({
            defaultMessage: 'There was an error processing your request. Please try again later.',
            id: 'appAndSiteMarketplace.inventory.socketError'
          })
        );
        setState(prevState => ({
          ...prevState,
          isLoading: false,
          socketItems: undefined,
          socketResults: undefined,
          text: undefined
        }));
        return;
      }
      if (data.items.length === 0) {
        showNotification(
          'warning',
          formatMessage({
            defaultMessage: 'No results were found for the provided entries.',
            id: 'appAndSiteMarketplace.inventory.socketNoResults'
          })
        );
      }
      setState(prevState => ({
        ...prevState,
        socketItems: data.items,
        socketResults: data.results
      }));
    });
    return () => {
      offEvent(componentId);
    };
  }, [type]);

  if (socketItems) {
    const strictList = {
      publishers: isInclude ? config.whitelistedStrictPublishers : config.blacklistedStrictPublishers,
      searches: isInclude ? config.whitelistedStrictSearches : config.blacklistedStrictSearches
    };
    const merged = socketResults
      ? mergeStrictEntries(strictList, socketResults)
      : {
          publishers: [...new Set([...(strictList.publishers ?? []), ...socketItems])],
          searches: strictList.searches ?? []
        };
    setAppSiteConfigurationProps(
      isInclude
        ? { whitelistedStrictPublishers: merged.publishers, whitelistedStrictSearches: merged.searches }
        : { blacklistedStrictPublishers: merged.publishers, blacklistedStrictSearches: merged.searches }
    );

    setState(prevState => ({
      ...prevState,
      inventoryList: undefined,
      isLoading: false,
      socketItems: undefined,
      socketResults: undefined,
      text: prevState.invalidEntries ? prevState.text : undefined
    }));
  }

  const handleOperationChange = (value: State['operation']) => {
    setState(prevState => ({
      ...prevState,
      operation: value
    }));
  };

  const operationLabels: Record<InventoryOperation, string> = {
    exclude: formatMessage({
      defaultMessage: 'Exclude',
      id: 'appAndSiteMarketplace.inventory.exclude'
    }),
    include: formatMessage({
      defaultMessage: 'Include',
      id: 'appAndSiteMarketplace.inventory.include'
    })
  };

  const handleTextChange = (val: string) => {
    setState(prevState => ({
      ...prevState,
      invalidEntries: undefined,
      text: val
    }));
  };

  const addGenericToList = (list: string[]) => {
    const { domains, invalid, subdomains } = classifyGenericEntries(list);
    const next = applyGenericEntries(config, operation, { domains, subdomains });

    setAppSiteConfigurationProps(next.patch);
    // Invalid entries are listed under the textarea (instead of a toast) and the text is kept for the user to fix it
    notifyGenericEntriesResult(formatMessage, { invalidCount: 0, skippedSubdomains: next.skippedSubdomains });
    setState(prevState => ({
      ...prevState,
      invalidEntries: invalid.length ? invalid : undefined,
      text: invalid.length ? prevState.text : undefined
    }));
  };

  const handleAddToListClick = async () => {
    const list = parseBulkEntries(text);
    if (type === 'generic') {
      if (list.length) {
        addGenericToList(list);
      }
      return;
    }
    if (!list.length) {
      return;
    }
    const { invalid, valid } = classifySpecificEntries(list);
    setState(prevState => ({
      ...prevState,
      invalidEntries: invalid.length ? invalid : undefined,
      isLoading: valid.length > 0 ? true : prevState.isLoading
    }));
    if (valid.length) {
      await getInventoryItems(valid, 'specific');
    }
  };

  const removeRows = (includeIds: Set<string>, excludeIds: Set<string>) => {
    if (type === 'specific') {
      const included = deleteStrictRows(
        { publishers: config.whitelistedStrictPublishers, searches: config.whitelistedStrictSearches },
        includeIds
      );
      const excluded = deleteStrictRows(
        { publishers: config.blacklistedStrictPublishers, searches: config.blacklistedStrictSearches },
        excludeIds
      );
      setAppSiteConfigurationProps({
        blacklistedStrictPublishers: excluded.publishers,
        blacklistedStrictSearches: excluded.searches,
        whitelistedStrictPublishers: included.publishers,
        whitelistedStrictSearches: included.searches
      });
      return;
    }
    const without = (items: string[] | undefined, ids: Set<string>) => (items ?? []).filter(item => !ids.has(item));
    setAppSiteConfigurationProps({
      blacklistedGenericPublishers: without(config.blacklistedGenericPublishers, excludeIds),
      blacklistedPublishers: without(config.blacklistedPublishers, excludeIds),
      whitelistedGenericPublishers: without(config.whitelistedGenericPublishers, includeIds),
      whitelistedPublishers: without(config.whitelistedPublishers, includeIds)
    });
  };

  const handleDeleteItemClick = () => {
    const ids = new Set(deleteId === undefined ? [] : [deleteId]);
    removeRows(deleteType === 'include' ? ids : new Set(), deleteType === 'include' ? new Set() : ids);

    setState(prevState => ({
      ...prevState,
      deleteId: undefined,
      deleteType: undefined,
      isPromptOpen: false,
      selectedList: prevState.selectedList.filter(
        key => key !== getSelectionKey(deleteType === 'include' ? 'include' : 'exclude', deleteId ?? '')
      )
    }));
  };

  const handleTogglePrompt = (val: boolean, toggleType?: string, id?: string) => {
    setState(prevState => ({
      ...prevState,
      deleteId: id,
      deleteType: toggleType,
      isPromptOpen: val
    }));
  };

  const handleDeleteItem = (itemOperation: InventoryOperation, url: string) =>
    handleTogglePrompt(true, itemOperation, url);

  const allIncludedRows = useMemo(
    () => getInventoryRows(appSiteConfiguration, type, 'include'),
    [appSiteConfiguration, type]
  );

  const allExcludedRows = useMemo(
    () => getInventoryRows(appSiteConfiguration, type, 'exclude'),
    [appSiteConfiguration, type]
  );

  const includedRows = useMemo(() => filterRowsBySearch(allIncludedRows, searchText), [allIncludedRows, searchText]);

  const excludedRows = useMemo(() => filterRowsBySearch(allExcludedRows, searchText), [allExcludedRows, searchText]);

  const handleSelectionChange = (nextSelectedList: string[]) => {
    setState(prevState => ({
      ...prevState,
      selectedList: nextSelectedList
    }));
  };

  const handleDeleteSelectionClick = () => {
    removeRows(new Set(getSelectedIds(selectedList, 'include')), new Set(getSelectedIds(selectedList, 'exclude')));

    setState(prevState => ({
      ...prevState,
      deleteId: undefined,
      deleteType: undefined,
      isPromptOpen: false,
      selectedList: []
    }));
  };

  const handleImportStatusChange = useCallback((status: ImportStatus) => {
    setState(prevState => (prevState.importStatus === status ? prevState : { ...prevState, importStatus: status }));
  }, []);

  const handleSearchInputChange = (inputValue: string) => {
    setState(prevState => ({
      ...prevState,
      searchText: inputValue,
      selectedList: filterSelectionBySearch(prevState.selectedList, inputValue)
    }));
  };

  const handleResetTextClick = () => {
    setState(prevState => ({
      ...prevState,
      invalidEntries: undefined,
      text: undefined
    }));
  };

  const handleTextAreaBlur = () => {
    if (text) {
      const bulkEntriesArray = parseBulkEntries(text);
      setState(prevState => ({
        ...prevState,
        text: bulkEntriesArray.join('\n')
      }));
    }
  };

  return (
    <div className="inventory">
      <main>
        <InventoryDescription type={type} />
        <section className="inventory__content">
          <article className="inventory__contentConfig">
            <div className="inventory__contentConfigOperation">
              <div className="inventory__contentConfigLabel">
                {formatMessage({
                  defaultMessage: '1. Choose if you want to allow (include) or block (exclude) sites.',
                  id: 'appAndSiteMarketplace.inventory.operation.label'
                })}
              </div>
              <div className="inventory__contentConfigOperationButtons">
                {(['include', 'exclude'] as const).map(item => (
                  <button
                    aria-pressed={operation === item}
                    className={`inventory__contentConfigOperationButton inventory__contentConfigOperationButton--${item}${operation === item ? ' inventory__contentConfigOperationButton--selected' : ''}`}
                    id={`appAndSiteMarketplaceOperation_${item}_${type}_${id}`}
                    key={item}
                    onClick={() => handleOperationChange(item)}
                    type="button"
                  >
                    <i className={`fa-regular ${item === 'include' ? 'fa-circle-check' : 'fa-circle-xmark'}`} />
                    {operationLabels[item]}
                  </button>
                ))}
              </div>
            </div>
            <div className="inventory__contentConfigText">
              <div className="inventory__contentConfigTextHeader">
                <div className="inventory__contentConfigTextHeaderTexts">
                  <div className="inventory__contentConfigLabel">
                    {type === 'generic'
                      ? formatMessage({
                          defaultMessage: '2. Paste your sites here and add the elements to your list.',
                          id: 'appAndSiteMarketplace.inventory.textArea.title.generic'
                        })
                      : formatMessage({
                          defaultMessage: '2. Paste your keys here and add the elements to your list.',
                          id: 'appAndSiteMarketplace.inventory.textArea.title.specific'
                        })}
                  </div>
                  <div className="inventory__contentConfigTextHint">
                    {type === 'generic'
                      ? formatMessage({
                          defaultMessage:
                            'Enter domains or subdomains, one per line or comma separated. Up to 100 subdomains per list.',
                          id: 'appAndSiteMarketplace.inventory.textArea.hint.generic'
                        })
                      : formatMessage({
                          defaultMessage:
                            'Only keys and app bundles in the text::text format (e.g. 55102::site_key), or app bundle IDs (e.g. com.example.app, id389801252), one per line or comma separated.',
                          id: 'appAndSiteMarketplace.inventory.textArea.hint.specific'
                        })}
                  </div>
                </div>
                <ButtonUI
                  disabled={!text}
                  icon="fa-solid fa-rotate-right"
                  id={`appAndSiteMarketplaceReset-${id}`}
                  onClick={handleResetTextClick}
                  shape="link"
                  size="ultraSmall"
                  title={formatMessage({
                    defaultMessage: 'Clear text',
                    id: 'appAndSiteMarketplace.inventory.textArea.reset'
                  })}
                  type="secondary"
                />
              </div>
              <div
                className={`inventory__contentConfigTextAreaWrapper${invalidEntries ? ' inventory__contentConfigTextAreaWrapper--error' : ''}`}
              >
                <TextArea
                  className="inventory__contentConfigTextArea"
                  height={190}
                  id={`appAndSiteMarketplaceTextArea_${id}`}
                  key="bulkSearchTextArea"
                  maxLength={type === 'generic' ? GENERIC_TEXT_MAX_LENGTH : SPECIFIC_TEXT_MAX_LENGTH}
                  onBlur={handleTextAreaBlur}
                  onChange={handleTextChange}
                  placeholder={formatMessage({
                    defaultMessage: 'Write here the site, domain or any word that you consider relevant...',
                    id: 'appAndSiteMarketplace.inventory.textArea.placeholder'
                  })}
                  value={text}
                />
                <span className="inventory__contentConfigTextAreaCounter">{`${text?.length ?? 0}/${type === 'generic' ? GENERIC_TEXT_MAX_LENGTH : SPECIFIC_TEXT_MAX_LENGTH}`}</span>
              </div>
              {invalidEntries && (
                <div className="inventory__contentConfigTextError" role="alert">
                  {formatMessage(
                    {
                      defaultMessage:
                        '{count, plural, one {This entry is not valid and was not added:} other {These entries are not valid and were not added:}} {entries}',
                      id: 'appAndSiteMarketplace.inventory.textArea.invalidEntries'
                    },
                    {
                      count: invalidEntries.length,
                      entries: invalidEntries.join(', ')
                    }
                  )}
                </div>
              )}
            </div>
            <ButtonUI
              className="inventory__contentConfigAdd"
              disabled={!parseBulkEntries(text).length}
              icon="fa-solid fa-plus"
              id={`appAndSiteMarketplaceAdd-${id}`}
              loading={isLoading}
              onClick={handleAddToListClick}
              shape="squareRounded"
              size="small"
              text={formatMessage({
                defaultMessage: 'Add elements to list',
                id: 'appAndSiteMarketplace.inventory.addElements'
              })}
              type="primary"
            />
            <div className="inventory__contentConfigDivider">
              {formatMessage({
                defaultMessage: 'OR',
                id: 'appAndSiteMarketplace.inventory.or'
              })}
            </div>
            <div className="inventory__contentConfigUpload">
              <AppAndSiteMarketplaceImport
                entityId={entityId}
                onStatusChange={handleImportStatusChange}
                sessionId={sessionId}
                type={type}
                userId={user.id!}
              />
            </div>
          </article>
          <article className="inventory__contentList">
            <InventoryList
              allExcludedRows={allExcludedRows}
              allIncludedRows={allIncludedRows}
              excludedRows={excludedRows}
              id={id}
              importStatus={importStatus}
              includedRows={includedRows}
              onDeleteItem={handleDeleteItem}
              onDeleteSelection={() => handleTogglePrompt(true)}
              onSearchChange={handleSearchInputChange}
              onSelectionChange={handleSelectionChange}
              searchText={searchText}
              selectedList={selectedList}
              type={type}
            />
          </article>
        </section>
      </main>
      {isPromptOpen && (
        <Prompt
          buttons={[
            {
              onClick: deleteType ? () => handleDeleteItemClick() : () => handleDeleteSelectionClick(),
              text: formatMessage({
                defaultMessage: 'Delete',
                id: 'common.delete'
              }),
              type: 'primary'
            }
          ]}
          closePrompt={() => handleTogglePrompt(false)}
          description={formatMessage({
            defaultMessage: 'This action cannot be undone and your changes will be lost.',
            id: 'appAndSiteMarketplace.inventory.promptDescription'
          })}
          icon="fas fa-exclamation-triangle"
          title={formatMessage({
            defaultMessage: 'Are you sure you want to delete?',
            id: 'appAndSiteMarketplace.inventory.promptTitle'
          })}
        />
      )}
    </div>
  );
}
